<template>
  <div class="row g-2 justify-content-center my-5">
    <div class="col-xxl-6 col-xl-7 col-lg-8 col-md-11 hyphenate">
      <h1>Bahn-Vorhersage</h1>
      <p>
        Bahn-Vorhersage ist ein Rankingsystem für Zugverbindungen. Es ermittelt für alle
        Zugverbindungen innerhalb Deutschlands einen sogenannten Verbindungsscore. Dieser gibt an,
        wie sicher die Anschlüsse einer Zugverbindung sind. Ein hoher Score bedeutet, dass
        voraussichtlich alle Anschlusszüge erreicht werden, ein niedriger Score bedeutet, dass
        möglicherweise nicht alle Anschlusszüge erreicht werden können.
      </p>
      <p>
        Ein hoher Verbindungsscore ist jedoch keine Garantie dafür, dass eine Verbindung
        funktioniert!
      </p>
      <div class="row gx-2 my-5 align-items-center">
        <div class="col-sm-8">
          <h3>Verbindungsscore</h3>
          <p>
            Mit dem Verbindungsscore hast du immer einen schnellen Überblick darüber, wie sicher die
            Anschlüsse in den Zugverbindungen sind.
          </p>
          <p>
            Bei einem Verbindungsscore von 90 % besteht eine berechnete Wahrscheinlichkeit von 10 %,
            dass mindestens ein Anschluss nicht erreicht wird.
          </p>
          <p>Direktverbindungen werden separat markiert, da kein Anschluss verpasst werden kann.</p>
        </div>
        <div class="col-sm-4 d-flex flex-wrap gap-2 justify-content-center">
          <transfer-score class="btn border rounded" :score="'cancelled'"></transfer-score>
          <transfer-score class="btn border rounded" :score="0.5"></transfer-score>
          <transfer-score class="btn border rounded" :score="0.6"></transfer-score>
          <transfer-score class="btn border rounded" :score="0.7"></transfer-score>
          <transfer-score class="btn border rounded" :score="0.8"></transfer-score>
          <transfer-score class="btn border rounded" :score="0.9"></transfer-score>
          <transfer-score class="btn border rounded" :score="1"></transfer-score>
          <transfer-score class="btn border rounded" :score="'direct'"></transfer-score>
        </div>
      </div>
      <div class="row gx-2 my-5 flex-sm-row-reverse align-items-center">
        <div class="col-sm-8">
          <h3>Pünktlichkeit</h3>
          <p>
            Für den letzten Zug in der Verbindung wird die Pünktlichkeit am Zielbahnhof
            vorhergesagt. Das gibt dir einen schnellen Überblick, ob du bei funktionierenden
            Anschlüssen und Direktverbindungen mit Verspätung rechnen musst.
          </p>
          <p>
            Angegeben wird die vorhergesagte Wahrscheinlichkeit, dass der finale Zug maximal 10 Min.
            zusätzliche Verspätung bekommt. Wichtig ist dabei "zusätzlich": Wenn der Zug bereits 5
            Min. verspätet ist, gibt die Pünktlichkeitsvorhersage die Wahrscheinlichkeit an, dass er
            mit maximal 15 Min. Verspätung ankommt. Bei Planung weit im Voraus ist noch keine
            aktuelle Verspätung bekannt, sodass die Vorhersage die Wahrscheinlichkeit angibt, dass
            der Zug mit maximal 10 Min. Verspätung ankommt.
          </p>
        </div>
        <div class="col-sm-4 d-flex flex-wrap gap-2 justify-content-center">
          <transfer-score class="btn border rounded" type="arrival" :score="0.5"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.55"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.6"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.65"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.7"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.75"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.8"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.85"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.9"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="0.95"></transfer-score>
          <transfer-score class="btn border rounded" type="arrival" :score="1"></transfer-score>
        </div>
      </div>
      <h2>Funktionsweise</h2>
      <p>
        Die Webseite zeigt die gleichen Zugverbindungen wie die Seite der Bahn oder der DB
        Navigator. Der Verbindungsscore, den diese Seite zusätzlich zu den Informationen der
        Deutschen Bahn anzeigt, wird aus Zugverspätungsvorhersagen eines Machine-Learning-Modells
        und der Umsteigezeit berechnet.
      </p>
      <div class="row gx-2 my-5 flex-sm-row-reverse align-items-center">
        <div class="col-sm-8">
          <h3>Anschlusssicherheit</h3>
          <p>
            Für jeden Umstieg wird die Wahrscheinlichkeit berechnet, mit der der Anschluss erreicht
            wird. Dabei gehen wir davon aus, dass die minimale Umsteigezeit eingehalten wird. Diese
            wird dir von Bahn-Vorhersage für jeden Umstieg angezeigt.
          </p>
          <p>
            Für den ankommenden und den abfahrenden Zug sagt Bahn-Vorhersage mithilfe von Machine
            Learning die Wahrscheinlichkeit verschieden starker Verspätungen voraus. Je pünktlicher
            der ankommende Zug, je unzuverlässiger der abfahrende Zug und je länger die
            Umsteigezeit, desto höher ist die Wahrscheinlichkeit, den Anschluss zu erreichen.
          </p>
        </div>
        <div class="col-sm-4 d-flex flex-sm-column gap-4 justify-content-center align-items-center">
          <TimeHistogram
            width="110px"
            height=""
            :time="dayjs().hour(13).minute(10)"
            :prediction="fromPred"
          >
          </TimeHistogram>
          <div>
            <b class="fs-4"> <i class="icon icon-transfer"></i> 4 Min </b>
          </div>
          <TimeHistogram
            width="110px"
            height=""
            :time="dayjs().hour(13).minute(17)"
            :prediction="toPred"
          >
          </TimeHistogram>
        </div>
      </div>
      <p>
        Um die Machine-Learning-Modelle zu trainieren, sammeln wir seit Oktober 2020 in Echtzeit
        Abfahrtszeiten von Zügen. Unser Machine-Learning-Modell wird einmal pro Woche auf den Daten
        der vergangenen Wochen neu trainiert. So können aktuelle Verspätungstrends sowie Baustellen
        berücksichtigt werden.
      </p>
      <p>
        Zur Funktionsweise haben wir bereits einige Vorträge gehalten sowie Ausarbeitungen und Paper
        geschrieben. Diese finden sich zusammen mit dem gesamten Quellcode hier:
      </p>
      <p>
        <NuxtLink class="btn btn-primary w-100" :to="{ path: '/opensource', hash: '#content' }"
          ><i class="icon icon-gitlab"></i> Docs / Quellcode</NuxtLink
        >
      </p>
      <h2>Verkehrswende</h2>
      <p>
        Angesichts der sich verschärfenden Klimakatastrophe müssen auch im Verkehrssektor die
        klimaschädlichen Emissionen so schnell wie möglich auf null reduziert werden. Dazu und wegen
        vieler anderer Probleme, die durch das Auto verursacht werden, müssen Autofahrten vermieden
        und auf den Umweltverbund (Fuß, Rad, Bus und Bahn) verlagert werden.
      </p>
      <p>
        Da die Bahn in den letzten Jahrzehnten vor allem von der Union kaputtgespart wurde, stellt
        sie heute für viele Menschen keine Alternative zum Auto dar. Das Projekt Bahn-Vorhersage
        soll dazu beitragen, das Bahnfahren trotz häufiger Verspätungen attraktiv zu machen, indem
        besonders unzuverlässige Verbindungen vermieden werden. So wirkt die Bahn pünktlicher, als
        sie ist. Um die Zuverlässigkeit der Bahn tatsächlich zu erhöhen, muss das Schienennetz
        jedoch massiv ausgebaut werden. Durch eine höhere Kapazität können Fahrpläne mit mehr Puffer
        geplant werden, die Verspätungen besser abfangen.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import TransferScore from '~/components/JourneyDisplay/TransferScore.vue'
import TimeHistogram from '~/components/JourneyDisplay/TimeHistogram.vue'
import dayjs from 'dayjs'
import { type DelayPrediction } from '~/types/fptfTypes'

const toPred: DelayPrediction = {
  predictions: [
    0.00097, 0.00166, 0.01065, 0.43869, 0.21216, 0.09996, 0.06415, 0.03922, 0.02298, 0.01576,
    0.00965, 0.01002, 0.00811, 0.00609, 0.00614, 0.00506, 0.00509, 0.0031, 0.00454, 0.00419,
    0.00456, 0.00327, 0.0024, 0.00254, 0.00294, 0.00192, 0.00122, 0.00088, 0.00146, 0.00162,
    0.00346, 0.00148, 0.00208, 0.00197
  ],
  offset: 3,
  type: 'delayPrediction'
}

const fromPred: DelayPrediction = {
  predictions: [
    0.00216, 0.00367, 0.03922, 0.24836, 0.22269, 0.14348, 0.08625, 0.04803, 0.03018, 0.0246, 0.0172,
    0.01503, 0.01129, 0.01226, 0.01097, 0.00676, 0.0104, 0.007, 0.00617, 0.00391, 0.00506, 0.00537,
    0.00512, 0.00406, 0.00422, 0.00377, 0.00382, 0.002, 0.00272, 0.0025, 0.00282, 0.003, 0.00312,
    0.00281
  ],
  offset: 3,
  type: 'delayPrediction'
}
</script>
